<template>
  <div class="scanner-container">
    <video ref="video" class="scanner-video" playsinline autoplay muted></video>
    <canvas ref="canvas" class="scanner-canvas"></canvas>
    <div class="controls">
      <button v-if="!running" @click="startCamera">Включить</button>
      <button v-else @click="stopCamera">Выключить</button>
      <div class="cam-label">{{ cameraLabel || 'Камера не выбрана' }}</div>
      <div class="debug">
        <div>readyState: {{ videoReady }}</div>
        <div>size: {{ videoSize.w }} x {{ videoSize.h }}</div>
      </div>
    </div>
    <div v-if="decoded" class="result">
      Найден QR-код: {{ decoded }}
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'
import jsQR from 'jsqr'


const video = ref(null)
const canvas = ref(null)

const decoded = ref('')
const error = ref('')
const cameraLabel = ref('')
const running = ref(false)
const videoSize = ref({ w: 0, h: 0 })
const videoReady = ref(0)

let stream = null
let rafId = null
let offscreen = null // used to capture frames for processing without drawing video to the visible canvas

async function startCamera() {
  error.value = ''
  decoded.value = ''

  try {
     // Request a higher (ideal) resolution to improve QR detection reliability
     stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } })
  video.value.srcObject = stream

    // Попробуем определить метку устройства
    const track = stream.getVideoTracks()[0]
    cameraLabel.value = track.label || ''

  // Подождём пока видео начнёт играть
  await video.value.play()

  // Создаём offscreen canvas для обработки кадров (не видимый пользователю)
  offscreen = document.createElement('canvas')

  running.value = true

  tick()
  } catch (e) {
    error.value = e.message || String(e)
  }
}

function stopCamera() {
  decoded.value = ''
  cameraLabel.value = ''
  running.value = false

  if (rafId) { cancelAnimationFrame(rafId); rafId = null }
  if (stream) {
    stream.getTracks().forEach(t => t.stop())
    stream = null
  }
}

function drawOverlay(ctx, w, h, location) {
  ctx.clearRect(0,0,w,h)
  if (!location) return
  ctx.lineWidth = 4
  ctx.strokeStyle = 'lime'
  ctx.beginPath()
  const corners = ['topLeftCorner','topRightCorner','bottomRightCorner','bottomLeftCorner']
  corners.forEach((c, i) => {
    const p = location[c]
    if (i===0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y)
  })
  ctx.closePath()
  ctx.stroke()
}

function tick() {
  if (!video.value || video.value.readyState !== video.value.HAVE_ENOUGH_DATA) {
    rafId = requestAnimationFrame(tick)
    return
  }

  const vw = video.value.videoWidth
  const vh = video.value.videoHeight

  // expose for debugging
  videoSize.value.w = vw
  videoSize.value.h = vh
  try { videoReady.value = video.value.readyState } catch (e) { videoReady.value = 0 }

    // ensure offscreen exists (in case startCamera didn't create it yet)
    if (!offscreen) offscreen = document.createElement('canvas')

    // Получаем размеры, в которых видео отображается в документе (CSS-пиксели)
    const rect = video.value.getBoundingClientRect()
    const cw = Math.max(1, Math.round(rect.width))
    const ch = Math.max(1, Math.round(rect.height))

    // Настроим видимый canvas (overlay) в размере отображаемой области (CSS-пиксели)
    const overlayCtx = canvas.value.getContext('2d')
    const dpr = window.devicePixelRatio || 1
    // internal pixel size scaled by DPR for crisp lines and correct mapping
    canvas.value.width = Math.round(cw * dpr)
    canvas.value.height = Math.round(ch * dpr)
    // keep CSS size
    canvas.value.style.width = cw + 'px'
    canvas.value.style.height = ch + 'px'
    // scale drawing context so we can use CSS-pixel coordinates when drawing
    overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
    overlayCtx.clearRect(0, 0, cw, ch)

    // Используем offscreen canvas для получения пикселей кадра и распознавания (в исходных пикселях)
    offscreen.width = vw
    offscreen.height = vh
    const offCtx = offscreen.getContext('2d')
    offCtx.drawImage(video.value, 0, 0, vw, vh)
    const imageData = offCtx.getImageData(0, 0, vw, vh)
    const code = jsQR(imageData.data, imageData.width, imageData.height)

    if (code) {
      // Когда используется object-fit: cover, видео может быть обрезано. Вычислим масштаб и смещение
      const scale = Math.max(cw / vw, ch / vh)
      const sw = vw * scale
      const sh = vh * scale
      const offsetX = (cw - sw) / 2
      const offsetY = (ch - sh) / 2

      // Преобразуем координаты из координат исходного кадра (пиксели видео) в координаты overlay (CSS-пиксели)
      const mappedLocation = {}
      for (const key of ['topLeftCorner','topRightCorner','bottomRightCorner','bottomLeftCorner']) {
        const p = code.location[key]
        mappedLocation[key] = { x: p.x * scale + offsetX, y: p.y * scale + offsetY }
      }

      drawOverlay(overlayCtx, cw, ch, mappedLocation)
      decoded.value = code.data
    }

  rafId = requestAnimationFrame(tick)
}

// Do not auto-start the camera; user will click "Включить"
// onMounted(() => {
//   startCamera()
// })

onBeforeUnmount(() => {
  stopCamera()
})
</script>

<style scoped>
.scanner-container {
  position: relative;
  width: 100%;
  /* leave room for header/footer but fill most of viewport */
  height: calc(100vh - 120px);
  max-height: 88vh;
  overflow: hidden;
}

.scanner-video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
  /* fill the container while preserving aspect ratio (may crop) */
  object-fit: cover;
  object-position: center center;
      background: black; /* fallback to avoid flashes */
      display: block;
}

.scanner-canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      background: transparent;
  z-index: 2; /* overlay above video but below UI elements */
}

.result {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  text-align: center;
  z-index: 3;
}

.controls {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  z-index: 4;
}

.controls button {
  background: rgba(0,0,0,0.6);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.controls .cam-label {
  color: #fff;
  font-size: 12px;
  opacity: 0.9;
}

.controls .debug {
  color: #fff;
  font-size: 11px;
  margin-left: 8px;
  opacity: 0.9;
}
</style>